/**
 * AlertManager.js — Proctoring Alert & Malpractice Detection Module
 * 
 * Features:
 * - Monitors tab switching (`visibilitychange`)
 * - Monitors window minimize / loss of focus (`blur`)
 * - Monitors fullscreen exit (`fullscreenchange`)
 * - Cooldown & debouncing to prevent duplicate alerts on simultaneous events
 * - Multi-strike counter (1 of 3, 2 of 3, etc.)
 * - Zero native alert() — renders custom site-aligned glassmorphic warning modal
 * - Dispatches 'contestAlertTriggered' custom event
 */

class AlertManager {
	constructor(options = {}) {
		this.options = Object.assign({
			maxStrikes: 3,
			cooldownMs: 3000,
			showAlert: true,
			oneAlertPerWindow: false,
			isWindowActive: null,
			getTestQuestionId: null,
			getElapsed: null,
			onAlert: null,
			onMaxStrikesExceeded: null
		}, options);

		if (this.options.OneAlertPerWindow !== undefined && this.options.oneAlertPerWindow === undefined) {
			this.options.oneAlertPerWindow = this.options.OneAlertPerWindow;
		}

		this.strikes = 0;
		this.alertHistory = [];
		this.isMonitoring = false;
		this.lastAlertTime = 0;
		this.activeModal = null;
		this.blurCheckTimeout = null;

		// Bind event handlers
		this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
		this.handleWindowBlur = this.handleWindowBlur.bind(this);
		this.handleFullscreenChange = this.handleFullscreenChange.bind(this);
	}

	startMonitoring() {
		if (this.isMonitoring) return;
		this.isMonitoring = true;

		document.addEventListener('visibilitychange', this.handleVisibilityChange);
		window.addEventListener('blur', this.handleWindowBlur);
		document.addEventListener('fullscreenchange', this.handleFullscreenChange);
		document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);

		console.log("🛡️ [AlertManager] Proctoring event monitoring active.");
	}

	stopMonitoring() {
		if (!this.isMonitoring) return;
		this.isMonitoring = false;

		document.removeEventListener('visibilitychange', this.handleVisibilityChange);
		window.removeEventListener('blur', this.handleWindowBlur);
		document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
		document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);

		this.dismissModal();
		if (this.blurCheckTimeout) {
			clearTimeout(this.blurCheckTimeout);
			this.blurCheckTimeout = null;
		}
		console.log("🛡️ [AlertManager] Proctoring event monitoring stopped.");
	}

	handleVisibilityChange() {
		if (!this.isMonitoring) return;
		if (document.hidden) {
			this.triggerAlert('TAB_SWITCH', 'Student switched browser tabs or minimized window.');
		}
	}

	handleWindowBlur() {
		if (!this.isMonitoring) return;
		// Ignore if document is already hidden (visibilitychange handled it)
		if (document.hidden) return;

		// Clear any existing check
		if (this.blurCheckTimeout) {
			clearTimeout(this.blurCheckTimeout);
		}

		// Allow browser event queue (100ms) to settle focus state and activeElement
		this.blurCheckTimeout = setTimeout(() => {
			this.blurCheckTimeout = null;
			if (!this.isMonitoring || document.hidden) return;

			// 1. If document.hasFocus() is true, the browser window itself still has focus!
			if (document.hasFocus && document.hasFocus()) {
				return;
			}

			// 2. Check if focus moved into the test question iframe
			const activeEl = document.activeElement;
			const isInsideIframe = activeEl && (
				activeEl.tagName === 'IFRAME' ||
				activeEl.id === 'quesIframe' ||
				(activeEl.closest && activeEl.closest('iframe'))
			);
			if (isInsideIframe) {
				return; // Legitimate interaction with test questions
			}

			// 3. Genuine loss of focus (clicked away to external desktop app or opened another window)
			this.triggerAlert('WINDOW_BLUR', 'Test window lost focus (clicked outside or opened another app).');
		}, 100);
	}

	handleFullscreenChange() {
		if (!this.isMonitoring) return;
		const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement;
		if (!isFullscreen && this.options.requireFullscreen) {
			this.triggerAlert('FULLSCREEN_EXIT', 'Student exited full-screen test mode.');
		}
	}

	triggerAlert(alertType, description) {
		const now = Date.now();
		// Debounce: ignore duplicate triggers fired within cooldown period
		if (now - this.lastAlertTime < this.options.cooldownMs) {
			return;
		}
		this.lastAlertTime = now;

		const isWindowActive = typeof this.options.isWindowActive === 'function' && !!this.options.isWindowActive();
		const oneAlertPerWindow = !!(this.options.oneAlertPerWindow || this.options.OneAlertPerWindow);
		const showAlert = this.options.showAlert !== false;

		// If oneAlertPerWindow is active and an incident recording window is already active:
		// Do not increment strikes and suppress the alert modal, but extend capture via onAlert.
		const isSuppressedSubAlert = oneAlertPerWindow && isWindowActive;

		if (!isSuppressedSubAlert) {
			this.strikes += 1;
		}

		const questionId = typeof this.options.getTestQuestionId === 'function' ? this.options.getTestQuestionId() : null;
		const elapsed = typeof this.options.getElapsed === 'function' ? this.options.getElapsed() : '00:00';

		const alertRecord = {
			strikeNumber: this.strikes,
			maxStrikes: this.options.maxStrikes,
			alertType: alertType,
			description: description,
			timestamp: now,
			timeFormatted: new Date(now).toLocaleTimeString(),
			testQuestionId: questionId,
			elapsed: elapsed,
			isWindowExtension: isSuppressedSubAlert
		};

		this.alertHistory.push(alertRecord);
		console.warn(`🚨 [AlertManager] ALERT TRIGGERED (#${this.strikes}/${this.options.maxStrikes}${isSuppressedSubAlert ? ' - window extension' : ''}):`, alertRecord);

		// Render custom UI warning dialog if showAlert is enabled and not suppressed by oneAlertPerWindow
		if (showAlert && !isSuppressedSubAlert) {
			this.showWarningModal(alertRecord);
		} else {
			console.log(`ℹ️ [AlertManager] Warning modal suppressed (showAlert=${showAlert}, oneAlertPerWindow=${oneAlertPerWindow}, isWindowActive=${isWindowActive}).`);
		}

		// Dispatch custom event for video buffer & UI integration
		try {
			window.dispatchEvent(new CustomEvent('contestAlertTriggered', { detail: alertRecord }));
		} catch (e) {}

		// Invoke callbacks (triggers circularBuffer.triggerAlert to start/extend capture window)
		if (typeof this.options.onAlert === 'function') {
			this.options.onAlert(alertRecord);
		}

		if (this.strikes >= this.options.maxStrikes && !isSuppressedSubAlert && typeof this.options.onMaxStrikesExceeded === 'function') {
			this.options.onMaxStrikesExceeded(alertRecord);
		}
	}

	showWarningModal(alertRecord) {
		this.dismissModal();

		const modalContainer = document.createElement('div');
		modalContainer.id = 'proctor-alert-modal-container';
		modalContainer.className = 'proctor-alert-overlay';
		modalContainer.style.cssText = `
			position: fixed;
			top: 0;
			left: 0;
			width: 100vw;
			height: 100vh;
			background: rgba(15, 23, 42, 0.85);
			backdrop-filter: blur(8px);
			-webkit-backdrop-filter: blur(8px);
			z-index: 100000;
			display: flex;
			align-items: center;
			justify-content: center;
			animation: proctorFadeIn 0.25s ease-out;
			font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
		`;

		const isFinalStrike = alertRecord.strikeNumber >= alertRecord.maxStrikes;
		const badgeColor = isFinalStrike ? '#dc2626' : '#d97706';
		const badgeText = isFinalStrike ? 'FINAL WARNING' : `STRIKE ${alertRecord.strikeNumber} OF ${alertRecord.maxStrikes}`;

		modalContainer.innerHTML = `
			<div class="proctor-alert-card" style="
				background: #ffffff;
				border-radius: 12px;
				width: 90%;
				max-width: 480px;
				padding: 28px 24px;
				box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2);
				text-align: center;
				border-top: 6px solid ${badgeColor};
			">
				<div style="
					display: inline-block;
					background: ${isFinalStrike ? '#fee2e2' : '#fef3c7'};
					color: ${badgeColor};
					font-weight: 700;
					font-size: 11px;
					letter-spacing: 0.5px;
					padding: 4px 12px;
					border-radius: 9999px;
					margin-bottom: 12px;
				">
					⚠️ ${badgeText}
				</div>

				<h3 style="
					margin: 0 0 8px 0;
					color: #0f172a;
					font-size: 20px;
					font-weight: 700;
				">
					${alertRecord.alertType === 'TAB_SWITCH' ? 'Tab Switch Detected' : 'Test Window Lost Focus'}
				</h3>

				<p style="
					margin: 0 0 16px 0;
					color: #475569;
					font-size: 14px;
					line-height: 1.5;
				">
					You left or minimized the active test window. All navigation events are recorded, and video evidence has been flagged for proctor review.
				</p>

				<div style="
					background: #f8fafc;
					border: 1px solid #e2e8f0;
					border-radius: 8px;
					padding: 10px 14px;
					font-size: 12px;
					color: #64748b;
					margin-bottom: 20px;
					text-align: left;
				">
					<strong>Incident Time:</strong> ${alertRecord.timeFormatted}<br>
					<strong>Status:</strong> Evidence video clip buffer active
				</div>

				<button id="proctor-alert-ack-btn" type="button" style="
					background: #094a8f;
					color: #ffffff;
					font-weight: 600;
					font-size: 15px;
					border: none;
					border-radius: 8px;
					padding: 12px 24px;
					width: 100%;
					cursor: pointer;
					transition: background-color 0.2s ease;
					box-shadow: 0 4px 6px -1px rgba(9, 74, 143, 0.2);
				">
					Return to Test
				</button>
			</div>
		`;

		document.body.appendChild(modalContainer);
		this.activeModal = modalContainer;

		const ackBtn = modalContainer.querySelector('#proctor-alert-ack-btn');
		if (ackBtn) {
			ackBtn.addEventListener('click', () => {
				this.dismissModal();
			});
		}
	}

	dismissModal() {
		if (this.activeModal && this.activeModal.parentNode) {
			this.activeModal.parentNode.removeChild(this.activeModal);
			this.activeModal = null;
		}
	}

	getStrikes() {
		return this.strikes;
	}

	getHistory() {
		return this.alertHistory;
	}

	hasAlerts() {
		return this.strikes > 0;
	}

	reset() {
		this.strikes = 0;
		this.alertHistory = [];
		this.lastAlertTime = 0;
		this.dismissModal();
	}
}

export default AlertManager;



// WEBPACK FOOTER //
// ./src/util/AlertManager.js